/**
 * Read-only hours words for the bay and location cards (DNKADM-0495). Pure helpers over
 * the weekly day/shift shape the location hours and bay schedules share: `dayOfWeek`
 * 0=Sunday … 6=Saturday, times `HH:mm` in the location's local time.
 *
 * A week reads as lines of consecutive days with the same hours ("Mon–Fri", "9:00 AM–5:00
 * PM"); a day that is not available, or has no shifts, reads "Closed".
 */
import { clock12 } from '@/utils/clockTime';

interface ShiftLike {
  startTime: string;
  endTime: string;
}

interface DayLike {
  dayOfWeek: number;
  isAvailable: boolean;
  shifts: ShiftLike[];
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `"9:00 AM"` from `"09:00"` (or `"09:00:00"`); the raw text when it does not parse. */
export function timeLabel(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm.trim());
  if (!match) return hhmm;
  return clock12(Number(match[1]), Number(match[2]));
}

/** `"9:00 AM–12:00 PM, 1:00 PM–5:00 PM"` for a day's shifts; `"Closed"` for none. */
export function shiftsLabel(shifts: ShiftLike[]): string {
  if (shifts.length === 0) return 'Closed';
  return shifts.map((s) => `${timeLabel(s.startTime)}–${timeLabel(s.endTime)}`).join(', ');
}

function dayHours(day: DayLike): string {
  return day.isAvailable ? shiftsLabel(day.shifts) : 'Closed';
}

/** One line of a week: the day or run of days, and their hours. */
export interface HoursLine {
  days: string;
  hours: string;
}

/** The week as runs of consecutive days (Sunday first) that share the same hours. */
export function weekLines(days: DayLike[]): HoursLine[] {
  const ordered = [...days].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  const lines: HoursLine[] = [];
  let runStart: DayLike | null = null;
  let runEnd: DayLike | null = null;
  let runHours = '';
  const flush = () => {
    if (runStart === null || runEnd === null) return;
    const first = DAY_SHORT[runStart.dayOfWeek] ?? `Day ${runStart.dayOfWeek}`;
    const last = DAY_SHORT[runEnd.dayOfWeek] ?? `Day ${runEnd.dayOfWeek}`;
    lines.push({ days: runStart === runEnd ? first : `${first}–${last}`, hours: runHours });
  };
  for (const day of ordered) {
    const hours = dayHours(day);
    if (runEnd !== null && hours === runHours && day.dayOfWeek === runEnd.dayOfWeek + 1) {
      runEnd = day;
      continue;
    }
    flush();
    runStart = day;
    runEnd = day;
    runHours = hours;
  }
  flush();
  return lines;
}

/** Whether any day of the week is open with at least one shift. */
export function hasOpenDay(days: DayLike[]): boolean {
  return days.some((d) => d.isAvailable && d.shifts.length > 0);
}
